import { motion, useInView } from "framer-motion"
import { Link, graphql, useStaticQuery } from "gatsby"
import parse from "html-react-parser"
import React, { useEffect, useRef, useState } from "react"
import ImageOpt from "./ImageOpt"

const RelatedCards = ({ module }) => {
  const ref = useRef(null)
  const [hasPlayed, setHasPlayed] = useState(false)
  const isInView = useInView(ref, { once: true });

  const sectionShow = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }
  const animFade = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeInOut",
        delayChildren: 0.5,
        staggerChildren: 0.2,
      },
    },
  }

  useEffect(() => {
    if (isInView && !hasPlayed) {
      setHasPlayed(true)
    }
  }, [isInView, hasPlayed, setHasPlayed])

  const [isInView2, setIsInView] = useState(false)
  const sectionRef = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true)
            observer.unobserve(entry.target) // Optionally stop observing once the element is in view
          }
        })
      },
      { threshold: 0.1 } // Adjust this value based on when you want to trigger the animation
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  const selectCards = module.selectRelatedCards
  const value = useStaticQuery(graphql`
    query {
      wp {
        themeGeneralSettings {
          fluxDna {
            cards {
              cardType
              image {
                altText
                mediaItemUrl
                title
              }
              title
              link {
                target
                title
                url
              }
            }
          }
        }
      }
    }
  `)
  const cardsDataList = value.wp.themeGeneralSettings.fluxDna
  const order = [
    "Camps & lodges",
    "Itineraries",
    "Experiences",
    "Tanzania",
    "Promotions",
  ]
  if (module.hideSection === "no") {
    return (
      <motion.section
        initial="hidden"
        animate={isInView || hasPlayed ? "show" : "hidden"}
        variants={sectionShow}
        ref={sectionRef}
        className={`masonary py-90 lgscreen:py-70 mdscreen:py-50 bg-pattern-2 pattern-tyre ${
          isInView ? "reveal_anim" : ""
        } ${ module.extraClass ? ` ${module.extraClass}` : ""}`}
      >
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView || hasPlayed ? "show" : "hidden"}
          variants={sectionShow}
          className="container-fluid-lg relative z-9"
        >
          <div className="flex items-center flex-wrap w-full">
            {module?.heading && (
              <div className="w-4/12 xlscreen:w-full">
                <div className="max-w-[401px] xlscreen:max-w-none w-full title xlscreen:text-center mdscreen:px-20">
                  <h3>{module.heading}</h3>
                </div>
              </div>
            )}

            <div className="w-8/12 xl:pl-45 xlscreen:w-full xlscreen:pt-65">
              <div className="flex items-center w-full flex-row justify-center gap-45 mdscreen:flex-col">
                {cardsDataList.cards
                  .filter(item => selectCards.includes(item.cardType))
                  .slice(0, 2)
                  .map((item, i) => {
                    const height = i % 2 === 0 ? 307 : 362
                    const width = i % 2 === 0 ? 387 : 292
                    return (
                      <Link
                        to={item.link.url}
                        className={`img-box-${
                          i + 1
                        } block border-bg-offwhite relative`}
                        target={item.link.target}
                        key={i}
                      >
                        <div className="img-box">
                          {item?.image && (
                            <img
                              src={item.image.mediaItemUrl}
                              alt={item.image.altText}
                              height={height}
                              width={width}
                              loading="lazy"
                              className={`h-[${height}px] w-[${width}px] mdscreen:w-full object-cover object-center`}
                              style={{
                                height: `${height}px`,
                                width: `${width}px`,
                              }}
                            />
                          )}
                          {item?.title && (
                            <div className="absolute container-fluid bottom-0 left-0 pb-45 mdscreen:pb-25 w-full z-9">
                              <div className="title title-white text-center">
                                <h5>{item.title}</h5>
                              </div>
                            </div>
                          )}
                        </div>
                      </Link>
                    )
                  })}
              </div>
              {cardsDataList.cards
                .filter(item => selectCards.includes(item.cardType))
                .slice(2, 3)
                .map((item, i) => {
                  return (
                    <div
                      className="w-full max-w-[516px] mx-auto mt-[51px]"
                      key={2}
                    >
                      <Link
                        to={item.link.url}
                        className="img-box-3 block border-bg-offwhite relative"
                        target={item.link.target}
                      >
                        <div className="img-box">
                          {item?.image && (
                            <img
                              src={item.image.mediaItemUrl}
                              alt={item.image.altText}
                              height={252}
                              width={496}
                              loading="lazy"
                              className="h-[252px] w-[496px] mdscreen:w-full object-cover object-center"
                              style={{ height: "252px", width: "496px" }}
                            />
                          )}
                          {item?.title && (
                            <div className="absolute container-fluid bottom-0 left-0 pb-45 mdscreen:pb-25 w-full z-9">
                              <div className="title title-white text-center">
                                <h5>{item.title}</h5>
                              </div>
                            </div>
                          )}
                        </div>
                      </Link>
                    </div>
                  )
                })}
            </div>
          </div>
        </motion.div>
      </motion.section>
    )
  }
  return null
}

export default RelatedCards
export const PageRelatedCardsFragment = graphql`
  fragment PageRelatedCardsFragment on WpPage_Pagecontent_PageContent_RelatedCards {
    extraId
    extraClass
    hideSection
    selectRelatedCards
    heading
  }
`
export const ItinerariesRelatedCardsFragment = graphql`
  fragment ItinerariesRelatedCardsFragment on WpItineraries_Itinerariescontent_ItinerariesContent_RelatedCards {
    extraId
    extraClass
    hideSection
    selectRelatedCards
    heading
  }
`
export const ExperienceRelatedCardsFragment = graphql`
  fragment ExperienceRelatedCardsFragment on WpExperiences_Experiencecontent_ExperienceContent_RelatedCards {
    extraId
    extraClass
    hideSection
    selectRelatedCards
    heading
  }
`
export const DestinationRelatedCardsFragment = graphql`
  fragment DestinationRelatedCardsFragment on WpDestination_Destinationcontent_DestinationContent_RelatedCards {
    extraId
    extraClass
    hideSection
    selectRelatedCards
    heading
  }
`
